// ─────────────────────────────────────────────────────────────────────────────
// WP-15 REQ-135 / RF-162 — Admin › Notificaciones.
//
// ANTES: 12 toggles del demo (`AdminNotificacionesPage.tsx`) en `useState`, sin backend. El propio
// demo tampoco persistía nada y "Guardar" solo parpadeaba 2.5s. Los ids de esos toggles
// (`bri_launch`, `rec_sent`, …) no existen en ningún contrato: el servicio expone UN toggle global de
// correo y CUATRO categorías (`RecognitionNotificationCategory`), más el resumen mensual.
//
// AHORA: la pantalla edita la configuración REAL y jerárquica (RF-162), un nivel a la vez, con
// precedencia Site > BU > OU > Global. Se conserva el lenguaje visual `.adm-notif-*` del demo.
//
// Tri-estado deliberado: cada toggle puede estar "definido aquí" (on/off explícito) o **heredado**
// del nivel inferior. Un booleano plano no puede expresar esa diferencia, y aplanarla tendría un
// efecto destructivo: bastaría con abrir la pantalla de una OU y pulsar "Guardar" para congelar en
// ella una copia de todos los valores globales, de forma que un cambio posterior en Global ya no la
// alcanzaría nunca. Por eso se lee el documento CRUDO (`exists` + nulls) y no `/resolve`.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PREFERENCE_SCOPE,
  PREFERENCE_SCOPE_ORDER,
  PREFERENCE_SCOPE_KEY,
  useRecognitionSettings,
  useSaveRecognitionSettings,
  useResolvedRecognitionSettings,
  useRecognitionRows,
  useFrontlineCatalogs,
  MAIL_FREQUENCY,
} from '@/api/recognition';
import type {
  PreferenceScope,
  MailFrequency,
  RecognitionPreferenceDto,
  SaveRecognitionPreferenceRequest,
} from '@/api/recognition';
import { useOUs } from '@/hooks/useOUs';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { AdminSection, AdminToggle } from './adminShared';
import { NotificationRowsTable } from './NotificationRowsTable';
import { SystemBroadcastCard } from './SystemBroadcastCard';

/** Los campos booleanos editables del documento, en el orden en que se pintan. */
type ToggleField =
  | 'globalEnabled'
  | 'inAppEnabled'
  | 'teamsEnabled'
  | 'recognitionReceivedEnabled'
  | 'streakAndBadgesEnabled'
  | 'campaignsEnabled'
  | 'digestEnabled'
  | 'monthlySummaryEnabled';

/** Los 4 toggles individuales: SOLO operan con el global ON (RF-162). */
const CATEGORY_FIELDS: ToggleField[] = [
  'recognitionReceivedEnabled',
  'streakAndBadgesEnabled',
  'campaignsEnabled',
  'digestEnabled',
];

/**
 * Valor por defecto de cada campo cuando NADIE lo define en ningún nivel. Espejo exacto de los
 * `?? ...` de `NotificationPreferenceResolver.Resolve`: si estos dos se separan, la pantalla enseñaría
 * un estado que no es el que se aplica al enviar. El resumen mensual es el único OFF por defecto.
 */
const SYSTEM_DEFAULT: Record<ToggleField, boolean> = {
  globalEnabled: true,
  // WP-F4-b — encendidos, como los dibuja la maqueta y como se comportaban antes de tener puerta.
  inAppEnabled: true,
  teamsEnabled: true,
  recognitionReceivedEnabled: true,
  streakAndBadgesEnabled: true,
  campaignsEnabled: true,
  digestEnabled: true,
  monthlySummaryEnabled: false,
};

/** Clave i18n de la etiqueta/descripción de cada campo (`admin.notifications.<clave>.label|desc`). */
const FIELD_LABEL_KEY: Record<ToggleField, string> = {
  globalEnabled: 'master',
  inAppEnabled: 'inApp',
  teamsEnabled: 'teams',
  monthlySummaryEnabled: 'monthly',
  recognitionReceivedEnabled: 'cat.recognitionReceivedEnabled',
  streakAndBadgesEnabled: 'cat.streakAndBadgesEnabled',
  campaignsEnabled: 'cat.campaignsEnabled',
  digestEnabled: 'cat.digestEnabled',
};

/**
 * WP-F4 — Las cuatro opciones de «Frecuencia de envío (email)» de la maqueta, en su orden.
 *
 * 🔴 **La equivalencia se MIDIÓ, no se inventó, y el desajuste aparente se resolvió solo.** La
 * maqueta ofrece *Inmediato (por evento) · Resumen diario · Resumen semanal · Resumen quincenal*, y
 * el enum es `Daily | Weekly | Biweekly | None`: parecía que faltaba «Inmediato» y sobraba «None».
 * No sobra ninguno — `Frequency` gobierna **solo la cadencia del RESUMEN**
 * (`RunRecognitionDigestCommandHandler:194,200`), no los correos por evento, que van por
 * `globalEnabled` + las 4 categorías. Así que `None` («no agrupar») **es** exactamente el
 * «Inmediato (por evento)» de la maqueta, y su propia nota anti-spam lo dice con otras palabras:
 * *«lo marcado como "Resumen" no se manda solo — se agrupa en un único correo periódico»*.
 */
const FREQUENCY_OPTIONS = [
  { value: MAIL_FREQUENCY.None, key: 'immediate' },
  { value: MAIL_FREQUENCY.Daily, key: 'daily' },
  { value: MAIL_FREQUENCY.Weekly, key: 'weekly' },
  { value: MAIL_FREQUENCY.Biweekly, key: 'biweekly' },
] as const;

/**
 * Cadencia por defecto cuando nadie la define en ningún nivel. Espejo del `?? MailFrequency.Weekly`
 * de `NotificationPreferenceResolver:90` — si estos dos se separan, la pantalla enseñaría una
 * cadencia que no es la que se aplica.
 */
const SYSTEM_DEFAULT_FREQUENCY: MailFrequency = MAIL_FREQUENCY.Weekly;

/** Estado editable local: null = heredado de los niveles inferiores. */
type Draft = Record<ToggleField, boolean | null>;

const EMPTY_DRAFT: Draft = {
  globalEnabled: null,
  inAppEnabled: null,
  teamsEnabled: null,
  recognitionReceivedEnabled: null,
  streakAndBadgesEnabled: null,
  campaignsEnabled: null,
  digestEnabled: null,
  monthlySummaryEnabled: null,
};

function draftFrom(dto: RecognitionPreferenceDto | undefined): Draft {
  if (!dto) return { ...EMPTY_DRAFT };
  return {
    globalEnabled: dto.globalEnabled,
    inAppEnabled: dto.inAppEnabled,
    teamsEnabled: dto.teamsEnabled,
    recognitionReceivedEnabled: dto.recognitionReceivedEnabled,
    streakAndBadgesEnabled: dto.streakAndBadgesEnabled,
    campaignsEnabled: dto.campaignsEnabled,
    digestEnabled: dto.digestEnabled,
    monthlySummaryEnabled: dto.monthlySummaryEnabled,
  };
}

interface ToggleRowProps {
  field: ToggleField;
  /** Valor definido EN ESTE nivel; null = heredado. */
  own: boolean | null;
  /** Valor que se aplica de verdad (propio, heredado o el del sistema). */
  effective: boolean;
  disabled?: boolean;
  onChange: (field: ToggleField, value: boolean | null) => void;
}

/**
 * Una fila de toggle con su estado de herencia. Vive fuera de `AdminNotifications` a propósito: como
 * componente anidado, React lo trataría como un tipo nuevo en cada render y desmontaría la fila entera
 * (y su foco) cada vez que se toca cualquier otro toggle de la pantalla.
 */
function ToggleRow({ field, own, effective, disabled = false, onChange }: Readonly<ToggleRowProps>) {
  const { t } = useTranslation('recognition');
  const isInherited = own === null;
  const labelKey = FIELD_LABEL_KEY[field];

  return (
    <div className={`adm-notif-item ${effective && !disabled ? 'adm-notif-item--on' : ''}`}>
      <div className="adm-notif-item-left">
        <span className="adm-notif-item-label">
          {t(`admin.notifications.${labelKey}.label`)}
          {isInherited && (
            <span className="adm-notif-inherited-chip">{t('admin.notifications.inherited')}</span>
          )}
        </span>
        <span className="adm-notif-item-desc">{t(`admin.notifications.${labelKey}.desc`)}</span>
        {!isInherited && (
          <button type="button" className="adm-notif-reset-btn" onClick={() => onChange(field, null)}>
            <i className="fas fa-rotate-left" aria-hidden="true" /> {t('admin.notifications.resetToInherited')}
          </button>
        )}
      </div>
      <AdminToggle isOn={effective} disabled={disabled} onChange={(next) => onChange(field, next)} />
    </div>
  );
}

export function AdminNotifications() {
  const { t } = useTranslation('recognition');
  const ous = useOUs({ activeOnly: false });

  const [scope, setScope] = useState<PreferenceScope>(PREFERENCE_SCOPE.Global);
  // OU de contexto. Para el nivel OU ES el scope; para BU y Site solo acota el catálogo del que se
  // eligen las unidades (UserAdmin los expone por OU).
  const [ouContext, setOuContext] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);

  // Los ids de BU y Site NO son ids de OU: salen de las columnas buId/siteId del padrón y UserAdmin
  // los expone como catálogo (GET v1/users/org-catalogs). Ofrecer OUs para esos niveles producía
  // documentos cuyo ScopeId no casaba con ningún destinatario: se guardaban con 200 y no apagaban
  // nada, en el nivel de MAYOR precedencia y sin ninguna señal en pantalla.
  const catalogs = useFrontlineCatalogs(ouContext ?? undefined);

  const needsScopeId = scope !== PREFERENCE_SCOPE.Global;
  const usesCatalog = scope === PREFERENCE_SCOPE.Bu || scope === PREFERENCE_SCOPE.Site;
  // Global no lleva id; OU usa la propia OU; BU y Site usan la unidad elegida del catálogo.
  let scopeId: string | null = null;
  if (needsScopeId) scopeId = usesCatalog ? unitId : ouContext;

  const settings = useRecognitionSettings(scope, scopeId);
  const save = useSaveRecognitionSettings();

  /**
   * WP-F4-b · 6.3 — Las filas de las dos tablas del nivel. Vienen del servidor porque la lista es
   * DECLARADA allí: la maqueta dibuja trece y solo nueve corresponden a un aviso real.
   */
  const rows = useRecognitionRows(scope, scopeId);

  /**
   * Interruptores de fila tocados en esta sesión, por clave `plantilla:canal`. Estado APARTE del
   * `draft` de los toggles globales porque su unidad no es un campo del documento sino una clave de
   * un diccionario, y mezclarlos obligaría a inventar un campo por fila.
   */
  const [filaOverrides, setFilaOverrides] = useState<Record<string, boolean | null>>({});

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  /**
   * WP-F4 — Correos bloqueados. Estado aparte de `draft` porque no es un toggle de tres estados
   * (propio / heredado / sistema): es una lista, y NO se hereda — la compuerta hace la UNIÓN de
   * todas las listas del tenant.
   */
  const [bloqueados, setBloqueados] = useState<string[]>([]);
  const [correoNuevo, setCorreoNuevo] = useState('');
  /**
   * WP-F4 — Frecuencia de envío del resumen. Estado aparte de `draft` por el mismo motivo que
   * `bloqueados`: no es un toggle de tres estados, es un enum, y `null` aquí significa «heredado».
   *
   * 🔴 El backend YA la guardaba, la resolvía con precedencia y la APLICABA
   * (`RunRecognitionDigestCommandHandler:194,200`); lo único que faltaba era el control. El propio
   * `handleSave` lo documentaba: «esta pantalla no los edita y no debe perderlos».
   */
  const [frecuencia, setFrecuencia] = useState<MailFrequency | null>(null);
  const [saved, setSaved] = useState(false);

  const scopeKey = `${scope}:${scopeId ?? ''}`;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  // El borrador se siembra UNA vez por nivel. La versión anterior lo reemplazaba en cada cambio de
  // identidad de `settings.data`, así que cualquier refetch (p. ej. al volver el foco a la ventana)
  // descartaba en silencio los toggles que el admin acababa de mover y dejaba el botón "Guardar"
  // deshabilitado, como si no hubiera cambiado nada.
  useEffect(() => {
    if (!settings.data || loadedKey === scopeKey) return;
    setDraft(draftFrom(settings.data));
    setBloqueados(settings.data.blockedEmails ?? []);
    setFrecuencia(settings.data.frequency ?? null);
    setLoadedKey(scopeKey);
    setSaved(false);
  }, [settings.data, scopeKey, loadedKey]);

  // Configuración efectiva de ESTE nivel una vez aplicada la precedencia hacia abajo. Es lo que se
  // enseña en un toggle heredado: sin ella no habría nada honesto que mostrar en esa fila.
  const resolved = useResolvedRecognitionSettings(
    {
      siteId: scope === PREFERENCE_SCOPE.Site ? scopeId : null,
      buId: scope === PREFERENCE_SCOPE.Bu ? scopeId : null,
      ouId: scope === PREFERENCE_SCOPE.Ou ? scopeId : null,
    },
    scope === PREFERENCE_SCOPE.Global || Boolean(scopeId),
  );

  const scopeReady = !needsScopeId || Boolean(scopeId);

  /** Valor a MOSTRAR: el propio si está definido aquí; si no, el heredado; si nadie lo define, el del sistema. */
  function effective(field: ToggleField): boolean {
    const own = draft[field];
    if (own !== null) return own;
    const inherited = resolved.data?.[field];
    return inherited ?? SYSTEM_DEFAULT[field];
  }

  const masterOn = effective('globalEnabled');

  /** Cadencia a MOSTRAR: la propia si está definida aquí; si no, la heredada; si nadie, la del sistema. */
  const frecuenciaEfectiva: MailFrequency =
    frecuencia ?? resolved.data?.frequency ?? SYSTEM_DEFAULT_FREQUENCY;
  const esInmediato = frecuenciaEfectiva === MAIL_FREQUENCY.None;

  const dirty = useMemo(() => {
    // Mientras el nivel actual no esté cargado no hay nada que perder. Sin esta guarda, al cambiar de
    // nivel el borrador conserva un instante los valores del nivel ANTERIOR y se compara con un
    // documento vacío: la pantalla se declaraba "sucia" y, como eso bloquea los selectores, un nivel
    // que aún no tiene unidad elegida (y por tanto nunca carga) dejaba al admin sin poder elegirla.
    if (loadedKey !== scopeKey) return false;
    const stored = draftFrom(settings.data);
    if ((Object.keys(EMPTY_DRAFT) as ToggleField[]).some((f) => stored[f] !== draft[f])) return true;
    // La lista de bloqueados también ensucia. Sin esto, añadir un correo dejaba «Guardar»
    // deshabilitado y el cambio se perdía al cambiar de nivel, sin aviso.
    // La frecuencia también ensucia: sin esto, cambiarla dejaba «Guardar» deshabilitado.
    if ((settings.data?.frequency ?? null) !== frecuencia) return true;
    // Los interruptores de fila también ensucian: sin esto, tocar uno dejaba «Guardar»
    // deshabilitado y el cambio se perdía al cambiar de nivel, sin aviso. Es el mismo defecto que ya
    // se pagó con la lista de bloqueados y con la frecuencia.
    if (Object.keys(filaOverrides).length > 0) return true;
    const guardados = settings.data?.blockedEmails ?? [];
    return guardados.length !== bloqueados.length
      || guardados.some((correo, i) => correo !== bloqueados[i]);
  }, [draft, bloqueados, frecuencia, filaOverrides, settings.data, loadedKey, scopeKey]);
  useUnsavedChangesGuard(dirty);

  /** Cambiar de nivel arranca de cero: el borrador del nivel anterior no debe viajar al siguiente. */
  function resetForNewScope() {
    setDraft(EMPTY_DRAFT);
    setBloqueados([]);
    setFilaOverrides({});
    setCorreoNuevo('');
    setFrecuencia(null);
    setLoadedKey(null);
    setSaved(false);
  }

  function setField(field: ToggleField, value: boolean | null) {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  }

  /** Descarta la edición en curso y vuelve a lo guardado en este nivel. */
  function discard() {
    setDraft(draftFrom(settings.data));
    setBloqueados(settings.data?.blockedEmails ?? []);
    setFrecuencia(settings.data?.frequency ?? null);
    setCorreoNuevo('');
    setSaved(false);
  }

  /**
   * Añade un correo a la lista. Se normaliza a minúsculas y sin espacios porque la compuerta compara
   * con `OrdinalIgnoreCase` (`NotificationPreferenceResolver.cs:98`): guardar «  Ana@X.com » y
   * «ana@x.com» crearía dos entradas que bloquean exactamente lo mismo, y el admin vería duplicados
   * que no puede distinguir.
   */
  function agregarBloqueado() {
    const correo = correoNuevo.trim().toLowerCase();
    if (!correo || bloqueados.includes(correo)) {
      setCorreoNuevo('');
      return;
    }
    setBloqueados((prev) => [...prev, correo]);
    setCorreoNuevo('');
    setSaved(false);
  }

  function quitarBloqueado(correo: string) {
    setBloqueados((prev) => prev.filter((c) => c !== correo));
    setSaved(false);
  }

  function handleSave() {
    // Se envía el documento COMPLETO del nivel: el backend hace ReplaceOne por (tenant, scope,
    // scopeId), así que un envío parcial borraría los campos omitidos. `ouFrequencyExceptions` se
    // reenvía tal cual llegó —es una excepción por OU que se define a nivel Global y esta pantalla
    // no la edita—; `frequency` y `blockedEmails` SÍ se editan aquí desde WP-F4.
    const body: SaveRecognitionPreferenceRequest = {
      scope,
      scopeId: needsScopeId ? scopeId : null,
      ...draft,
      frequency: frecuencia,
      ouFrequencyExceptions: settings.data?.ouFrequencyExceptions ?? null,
      blockedEmails: bloqueados,
      // 🔴 Se envía el diccionario COMPLETO: lo guardado más lo tocado en esta sesión. El documento
      // se reemplaza entero, así que mandar solo los cambios borraría los interruptores que ya
      // estaban puestos en este nivel — y un `null` significa «vuelve a heredar», que es una orden
      // distinta de «no me lo mandaron».
      typeChannelOverrides: overridesParaGuardar(),
    };
    save.mutate(body, { onSuccess: () => { setSaved(true); setFilaOverrides({}); } });
  }

  /**
   * WP-F4-b · 6.3 — El diccionario de interruptores que se manda al guardar: lo que ya estaba en el
   * nivel, con lo tocado en esta sesión encima.
   *
   * Un `null` en el estado local significa «vuelve a heredar»: se OMITE la clave, que es como el
   * servidor expresa la herencia. Mandarla con `true` sería otra cosa —un «encendido explícito» que
   * dejaría de seguir a su categoría—.
   */
  function overridesParaGuardar(): Record<string, boolean> {
    const resultado: Record<string, boolean> = {};

    for (const fila of rows.data ?? []) {
      for (const [canal, valor] of Object.entries(fila.channels)) {
        if (valor !== null && valor !== undefined) resultado[`${fila.key}:${canal}`] = valor;
      }
    }

    for (const [clave, valor] of Object.entries(filaOverrides)) {
      if (valor === null) delete resultado[clave];
      else resultado[clave] = valor;
    }

    return resultado;
  }

  /** Props comunes de cada fila, para no repetir el cableado en los seis sitios donde se pinta. */
  const rowProps = (field: ToggleField) => ({
    field,
    own: draft[field],
    effective: effective(field),
    onChange: setField,
  });

  return (
    <AdminSection title={t('admin.notifications.title')} description={t('admin.notifications.desc')}>
      {/* Selector de nivel. La precedencia se explica en la pantalla porque el efecto de guardar en un
          nivel u otro no es evidente: lo que se escriba aquí puede quedar tapado por un nivel superior. */}
      <div className="adm-notif-scope">
        <label className="adm-notif-scope-field">
          <span className="adm-notif-scope-label">{t('admin.notifications.scopeTitle')}</span>
          <select
            className="adm-notif-freq-select"
            value={scope}
            disabled={dirty}
            onChange={(e) => {
              setScope(Number(e.target.value) as PreferenceScope);
              setUnitId(null);
              resetForNewScope();
            }}
          >
            {PREFERENCE_SCOPE_ORDER.map((s) => (
              <option key={s} value={s}>{t(`admin.notifications.scope.${PREFERENCE_SCOPE_KEY[s]}`)}</option>
            ))}
          </select>
        </label>

        {needsScopeId && (
          <label className="adm-notif-scope-field">
            <span className="adm-notif-scope-label">
              {usesCatalog ? t('admin.notifications.scopeOuContext') : t('admin.notifications.scopeUnit')}
            </span>
            <select
              className="adm-notif-freq-select"
              value={ouContext ?? ''}
              disabled={dirty}
              onChange={(e) => { setOuContext(e.target.value || null); setUnitId(null); resetForNewScope(); }}
            >
              <option value="">{t('admin.notifications.scopePick')}</option>
              {(ous.data ?? []).map((ou) => (
                <option key={ou.key} value={ou.key}>{ou.label}</option>
              ))}
            </select>
          </label>
        )}

        {usesCatalog && (
          <label className="adm-notif-scope-field">
            <span className="adm-notif-scope-label">{t('admin.notifications.scopeUnit')}</span>
            <select
              className="adm-notif-freq-select"
              value={unitId ?? ''}
              disabled={dirty || !ouContext}
              onChange={(e) => { setUnitId(e.target.value || null); resetForNewScope(); }}
            >
              <option value="">
                {ouContext ? t('admin.notifications.scopePick') : t('admin.notifications.scopeOuFirst')}
              </option>
              {(scope === PREFERENCE_SCOPE.Bu
                ? catalogs.data?.businessUnits
                : catalogs.data?.sites
              )?.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Los selectores se bloquean con cambios sin guardar: cambiar de nivel con el borrador sucio
          los perdía en silencio, y lo que se pierde aquí es una configuración de envío. */}
      {dirty && (
        <p className="adm-notif-state" role="status">
          {t('admin.notifications.unsavedLock')}{' '}
          <button type="button" className="adm-notif-reset-btn" onClick={discard}>
            <i className="fas fa-xmark" aria-hidden="true" /> {t('admin.notifications.discard')}
          </button>
        </p>
      )}

      <div className="adm-notif-rules">
        <i className="fas fa-circle-info adm-notif-rules-icon" aria-hidden="true" />
        <div className="adm-notif-rules-content">
          <span className="adm-notif-rules-title">{t('admin.notifications.precedenceTitle')}</span>
          <ul className="adm-notif-rules-list">
            <li>{t('admin.notifications.precedence1')}</li>
            <li>{t('admin.notifications.precedence2')}</li>
            <li>{t('admin.notifications.precedence3')}</li>
          </ul>
        </div>
      </div>

      {/* Estados honestos: sin nivel elegido no se pide nada, y un fallo del servicio no se disfraza
          de «activado por omisión» — enseñar toggles inventados haría creer al admin que su ajuste
          está guardada. */}
      {needsScopeId && !scopeId && (
        <p className="adm-notif-state" role="status">{t('admin.notifications.scopeRequired')}</p>
      )}
      {scopeReady && (settings.isLoading || resolved.isLoading) && (
        <p className="adm-notif-state" role="status">{t('common.loading')}</p>
      )}
      {/* El editor exige AMBAS consultas. Sin `/resolve` no se conoce el valor heredado, y pintar
          entonces el del sistema haría que un maestro apagado en Global se mostrara ENCENDIDO con el
          chip «Heredado»: el admin leería "los correos salen" justo cuando no sale ninguno. */}
      {scopeReady && (settings.isError || resolved.isError) && (
        <p className="adm-notif-state" role="alert">{t('admin.notifications.loadError')}</p>
      )}

      {scopeReady && !settings.isLoading && !settings.isError && !resolved.isLoading && !resolved.isError && (
        <>
          <div className="adm-notif-groups">
            {/* WP-F4-b · 6.4 — «Avisos del sistema», arriba del todo como en la maqueta. Va DENTRO
                del bloque de grupos y no en su propia sección porque comparte el lenguaje visual de
                las demás tarjetas — pero no depende del nivel elegido: un mantenimiento no se
                configura por OU, se manda. */}
            <SystemBroadcastCard />

            <div className="adm-notif-group">
              <div className="adm-notif-group-head">
                <div className="adm-notif-group-title-row">
                  <span className="adm-notif-group-dot adm-notif-group-dot--email" />
                  <h3 className="adm-notif-group-title">{t('admin.notifications.group.email')}</h3>
                </div>
              </div>
              <div className="adm-notif-items">
                <ToggleRow {...rowProps('globalEnabled')} />

                {/* WP-F4-b — los otros dos canales de la maqueta. 🔴 NO se deshabilitan con el
                    maestro apagado, al revés que las categorías: el maestro es el del CORREO, y
                    atenuarlos sugeriría que dependen de él cuando no lo hacen. */}
                <ToggleRow {...rowProps('inAppEnabled')} />
                <ToggleRow {...rowProps('teamsEnabled')} />

                {/* WP-F4 — «Frecuencia de envío (email)». El backend ya la guardaba, la resolvía con
                    precedencia y la APLICABA; no había control. Va aquí porque la maqueta la pone en
                    la primera fila de globales, junto al interruptor de correo. */}
                <div className="adm-notif-item">
                  <div className="adm-notif-item-left">
                    <span className="adm-notif-item-label">
                      {t('admin.notifications.frequency.label')}
                      {frecuencia === null && (
                        <span className="adm-notif-inherited-chip">{t('admin.notifications.inherited')}</span>
                      )}
                    </span>
                    <span className="adm-notif-item-desc">{t('admin.notifications.frequency.desc')}</span>
                    {frecuencia !== null && (
                      <button
                        type="button"
                        className="adm-notif-reset-btn"
                        onClick={() => { setFrecuencia(null); setSaved(false); }}
                      >
                        <i className="fas fa-rotate-left" aria-hidden="true" />{' '}
                        {t('admin.notifications.resetToInherited')}
                      </button>
                    )}
                  </div>
                  <select
                    className="adm-notif-freq-select"
                    aria-label={t('admin.notifications.frequency.label')}
                    value={frecuenciaEfectiva}
                    disabled={!masterOn}
                    onChange={(e) => { setFrecuencia(Number(e.target.value) as MailFrequency); setSaved(false); }}
                  >
                    {FREQUENCY_OPTIONS.map((o) => (
                      <option key={o.key} value={o.value}>
                        {t(`admin.notifications.frequency.opt.${o.key}`)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 🔴 Dos interruptores para el mismo resultado, y hay que decirlo: con «Inmediato»
                    el resumen NO se manda, aunque su categoría siga encendida más abajo. Callarlo
                    dejaría al admin con la categoría «Resumen» en verde y ningún resumen saliendo —
                    la familia de defecto «se aprobó y no hizo nada». */}
                {esInmediato && masterOn && (
                  <p className="adm-notif-warn" role="note">
                    <i className="fas fa-circle-info" aria-hidden="true" />{' '}
                    {t('admin.notifications.frequency.immediateHint')}
                  </p>
                )}
              </div>
            </div>

            <div className="adm-notif-group">
              <div className="adm-notif-group-head">
                <div className="adm-notif-group-title-row">
                  <span className="adm-notif-group-dot adm-notif-group-dot--cats" />
                  <h3 className="adm-notif-group-title">{t('admin.notifications.group.categories')}</h3>
                  <span className="adm-notif-group-count">
                    {CATEGORY_FIELDS.filter((f) => masterOn && effective(f)).length}/{CATEGORY_FIELDS.length}{' '}
                    {t('admin.notifications.active')}
                  </span>
                </div>
              </div>
              {/* Los individuales solo operan con el global ON (RF-162): con el maestro apagado se
                  deshabilitan, para que no parezcan efectivos cuando no van a enviar nada. */}
              {!masterOn && (
                <p className="adm-notif-state" role="status">{t('admin.notifications.masterOffHint')}</p>
              )}
              <div className="adm-notif-items">
                {CATEGORY_FIELDS.map((field) => (
                  <ToggleRow key={field} {...rowProps(field)} disabled={!masterOn} />
                ))}
              </div>
            </div>

            <div className="adm-notif-group">
              <div className="adm-notif-group-head">
                <div className="adm-notif-group-title-row">
                  <span className="adm-notif-group-dot adm-notif-group-dot--summary" />
                  <h3 className="adm-notif-group-title">{t('admin.notifications.group.summary')}</h3>
                </div>
              </div>
              <div className="adm-notif-items">
                <ToggleRow {...rowProps('monthlySummaryEnabled')} disabled={!masterOn} />
              </div>
            </div>

            {/* WP-F4 — Bloqueo por correo. El backend ya lo guardaba, lo leía y lo APLICABA
                (`RecognitionEmailGate`), pero no había forma de rellenar la lista: una compuerta
                viva sin interruptor. */}
            <div className="adm-notif-group">
              <div className="adm-notif-group-head">
                <div className="adm-notif-group-title-row">
                  <span className="adm-notif-group-dot adm-notif-group-dot--blocked" />
                  <h3 className="adm-notif-group-title">{t('admin.notifications.group.blocked')}</h3>
                </div>
                <p className="adm-notif-group-desc">{t('admin.notifications.blocked.desc')}</p>
                {/* 🔴 El aviso NO es decorativo. La compuerta hace la UNIÓN de todas las listas del
                    tenant (`preferences.Any(...)`), así que un correo bloqueado en una OU queda
                    bloqueado en TODAS. Sin decirlo, un admin de OU creería estar afectando solo a la
                    suya. Y deniega ANTES que el toggle global: es el bloqueo más fuerte que hay. */}
                <p className="adm-notif-warn" role="note">
                  <i className="fas fa-triangle-exclamation" aria-hidden="true" />{' '}
                  {t('admin.notifications.blocked.scopeWarning')}
                </p>
              </div>

              <div className="adm-notif-blocked-add">
                <input
                  type="email"
                  className="adm-notif-blocked-input"
                  value={correoNuevo}
                  placeholder={t('admin.notifications.blocked.placeholder')}
                  aria-label={t('admin.notifications.blocked.placeholder')}
                  onChange={(e) => setCorreoNuevo(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter añade sin enviar el formulario: es el gesto natural en una lista y sin
                    // él hay que ir al botón con el ratón después de cada correo.
                    if (e.key === 'Enter') { e.preventDefault(); agregarBloqueado(); }
                  }}
                />
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={agregarBloqueado}
                  disabled={correoNuevo.trim().length === 0}
                >
                  <i className="fas fa-plus" aria-hidden="true" /> {t('admin.notifications.blocked.add')}
                </button>
              </div>

              {bloqueados.length === 0 ? (
                <p className="adm-notif-blocked-empty">{t('admin.notifications.blocked.empty')}</p>
              ) : (
                <ul className="adm-notif-blocked-list">
                  {bloqueados.map((correo) => (
                    <li key={correo} className="adm-notif-blocked-item">
                      <span className="adm-notif-blocked-mail">{correo}</span>
                      <button
                        type="button"
                        className="adm-notif-blocked-remove"
                        onClick={() => quitarBloqueado(correo)}
                        aria-label={t('admin.notifications.blocked.remove', { email: correo })}
                      >
                        <i className="fas fa-xmark" aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* WP-F4-b · 6.3 — Las dos tablas de la maqueta. Se pintan solo cuando sus filas cargan:
              una tabla vacía mientras llega la respuesta se lee como «no hay nada configurable». */}
          {rows.data && rows.data.length > 0 && (
            <>
              <p className="adm-notif-state" role="note">
                <i className="fas fa-circle-info" aria-hidden="true" />{' '}
                {t('admin.notifications.tables.nineRows')}
              </p>
              <NotificationRowsTable
                table="shine"
                rows={rows.data}
                overrides={filaOverrides}
                onToggle={(clave, valor) => {
                  setFilaOverrides((prev) => ({ ...prev, [clave]: valor }));
                  setSaved(false);
                }}
              />
              <NotificationRowsTable
                table="admin"
                rows={rows.data}
                overrides={filaOverrides}
                onToggle={(clave, valor) => {
                  setFilaOverrides((prev) => ({ ...prev, [clave]: valor }));
                  setSaved(false);
                }}
              />
            </>
          )}

          {/* WP-F4-b · 6.5 — «Diseño de Emails». Dos muestras y una leyenda: la maqueta no dibuja
              NINGÚN control aquí, y su propio texto lo dice —«el diseño de cada correo se ajusta
              directamente en la plantilla, no desde aquí»—. Se pinta tal cual, sin inventar un
              editor: eso habría sido alcance que nadie pidió y un control que no gobierna nada. */}
          <div className="adm-notif-group">
            <div className="adm-notif-group-head">
              <div className="adm-notif-group-title-row">
                <span className="adm-notif-group-dot adm-notif-group-dot--email" />
                <h3 className="adm-notif-group-title">{t('admin.notifications.design.title')}</h3>
              </div>
            </div>
            <div className="adm-notif-design">
              <p className="adm-notif-item-desc">{t('admin.notifications.design.desc')}</p>
              <div className="adm-notif-design-grid">
                <div className="adm-notif-design-card">
                  <div className="adm-notif-design-head adm-notif-design-head--fund">
                    <div className="adm-notif-design-swatch adm-notif-design-swatch--fund">
                      {t('admin.notifications.design.fund.name')}
                    </div>
                  </div>
                  <div className="adm-notif-design-foot">
                    {t('admin.notifications.design.fund.style')}
                  </div>
                </div>
                <div className="adm-notif-design-card">
                  <div className="adm-notif-design-head adm-notif-design-head--shine">
                    <div className="adm-notif-design-swatch adm-notif-design-swatch--shine">
                      {t('admin.notifications.design.shine.name')}
                    </div>
                  </div>
                  <div className="adm-notif-design-foot">
                    {t('admin.notifications.design.shine.style')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="adm-notif-footer">
            {save.isError && (
              <span className="adm-notif-state" role="alert">{t('admin.notifications.saveError')}</span>
            )}
            {saved && !dirty && (
              <span className="adm-savebar-ok"><i className="fas fa-check" aria-hidden="true" /> {t('admin.notifications.saved')}</span>
            )}
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSave}
              disabled={!dirty || save.isPending}
            >
              <i className="fas fa-floppy-disk" aria-hidden="true" />{' '}
              {save.isPending ? t('admin.saving') : t('admin.save')}
            </button>
          </div>
        </>
      )}
    </AdminSection>
  );
}

export default AdminNotifications;
